import {inject} from '@angular/core';
import {HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest} from '@angular/common/http';
import {Observable, of, retry, tap, throwError, timer} from 'rxjs';
import {AuthService} from '../../modules/auth';
import {catchError} from 'rxjs/operators';
import {CacheInterceptorService} from "../services/cache.interceptor.service";
import {SweetAlertService} from "../services/sweet-alert.service";
import {AccessControl} from "../../../_config/access-control";

/**
 * Intercepts all HTTP requests and adds an Authorization header with a JWT token if available.
 * It also handles 401 errors with an invalid JWT token by logging out the user and redirecting to the login page.
 *
 * @author Carlos Duardo <carlos.duardo@qualud.com>
 */
export const appInterceptorFn: HttpInterceptorFn = (
  request: HttpRequest<any>,
  next: HttpHandlerFn): Observable<HttpEvent<any>> => {
  const sweetAlertService = inject(SweetAlertService);

  const authToken = AuthService.getAuthFromLocalStorage();

  if (null !== authToken) {
    request = request.clone({
      headers: request.headers.set('Authorization', 'Bearer ' + authToken)
    });
  }

  return next(request)
    .pipe(
      retry({
        count: 2,
        delay: (_, retryCount) => timer(retryCount * 1000)
      }),
      catchError((reason) => {
        if (!reason.url.includes('refresh_token')) {
          if (401 === reason.error.code && 'Invalid JWT Token' === reason.error.message) {
            sweetAlertService.info({text: AccessControl.onInvalidTokenMessage})
              .then(() => window.location.reload());
          }
        }

        return throwError(() => reason);
      })
    );
}


/**
 * Caching interceptor function that intercepts HTTP requests and responses.
 * It checks if the requested data is present in the cache, and if so, returns the cached response.
 * Otherwise, it forwards the request to the next handler and caches the response for future use.
 *
 * @author Carlos Duardo <carlos.duardo@qualud.com>
 */
export const cachingInterceptorFn: HttpInterceptorFn = (
  request: HttpRequest<any>,
  next: HttpHandlerFn): Observable<HttpEvent<any>> => {

  const cache = inject(CacheInterceptorService);

  const cached = cache.get(request.url);
  const isCacheHit = undefined !== cached;
  if (isCacheHit) {
    return of(cached);
  }

  return next(request)
    .pipe(
      tap((response) => cache.set(request.url, response))
    );
}
